import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Count of inbound unread direct_messages for the current user,
// kept live via realtime. Used for the ProfileSheet inbox badge and
// the bottom-bar chip. Cheap head-count query — no row payload.
export function useUnreadMessages() {
  const { user } = useAuth()
  const userId = user?.id || null
  const [count, setCount] = useState(0)

  const refetch = useCallback(async () => {
    if (!userId) { setCount(0); return }
    const { count: c } = await supabase
      .from('direct_messages')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .is('read_at', null)
    setCount(c || 0)
  }, [userId])

  useEffect(() => {
    if (!userId) { setCount(0); return undefined }
    let alive = true
    // Unique topic per hook instance. This hook mounts in more than
    // one place at once (bottom bar + ProfileSheet); a shared topic
    // name makes the second mount attach .on() to an already-
    // subscribed channel, which Realtime rejects with "cannot add
    // postgres_changes callbacks after subscribe()".
    const channel = supabase
      .channel(`dm-unread-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'direct_messages' },
        () => { if (alive) refetch() },
      )
      .subscribe()
    refetch()
    // Recount when the app returns to the foreground (read receipts
    // flipped on another device, etc.)
    const onVis = () => { if (document.visibilityState === 'visible') refetch() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      alive = false
      supabase.removeChannel(channel)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [userId, refetch])

  return count
}
